/* eslint-disable react/prop-types */
import { App, Button, Spin } from 'antd';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import File from '../../interfaces/File';
import { GetFilesPayload } from '../../interfaces/FileService';
import { FileService } from '../../services/fileService';
import { getFileTitle, getFolderHex } from '../../utils/utils';
import FileIcon from '../Common/FileIcon';
import { Icon } from '../Core/Icon';
import TruncatedTooltip from '../Common/TruncatedTooltip';
import User from '../../interfaces/User';

const fileService = new FileService();

interface Props {
  selectedFile: File;
  onCancel: () => void;
  onSubmit: (folderUid: number | null) => void;
  currentFolder?: File | null;
  onBehalfOfUser: User | null;
}

export const FileShortcutForm = ({
  selectedFile,
  onSubmit,
  onCancel,
  currentFolder,
  onBehalfOfUser,
}: Props) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [filters, setFilters] = useState<GetFilesPayload>({
    count: 25,
    fileTypes: [],
    list: 0,
    offset: 0,
    onlySigned: false,
    onlyUnsealed: false,
    orderAsc: false,
    orderColumn: 2,
    parent: undefined,
    priceFilter: 0,
    search: '',
    shareWith: [],
    tags: [],
    type: 2,
  });
  const [files, setFiles] = useState<File[]>([]);
  const [totalFiles, setTotalFiles] = useState<number>(0);
  const [selectedFolder, setSelectedFolder] = useState<File | null>(null);
  const [parent, setParent] = useState<File | undefined>();
  const listRef = useRef<HTMLUListElement>(null);

  /**
   * @desc Get the files
   * @param reset - Whether to reset the files
   */
  const getFiles = useCallback(
    async (reset = false) => {
      const payload: GetFilesPayload = {
        ...filters,
        parent: filters.parent || undefined,
      };

      if (onBehalfOfUser) {
        payload.onBehalfOf = onBehalfOfUser.userId;
      }

      setIsLoading(true);
      try {
        const res = await fileService.getFiles(payload);
        setFiles(reset ? res.myFiles : [...files, ...res.myFiles]);
        setTotalFiles(res.total);
        setParent(res?.parent || undefined);
      } catch {
        notification.error({
          message: t('errors.genericFetchError', {
            resource: t('common.files'),
          }),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [filters]
  );

  /**
   * @desc Handle the scroll event
   */
  const handleScroll = () => {
    if (listRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      if (
        scrollTop + clientHeight >= scrollHeight &&
        !isLoading &&
        files.length < totalFiles
      ) {
        setFilters({ ...filters, offset: filters.offset + filters.count });
      }
    }
  };

  useEffect(() => {
    setFilters((prevFilters) => ({ ...prevFilters, offset: 0 }));
  }, [selectedFile, filters.parent]);

  useEffect(() => {
    getFiles(true);
  }, [selectedFile, filters.parent]);

  useEffect(() => {
    if (filters.offset !== 0) {
      getFiles();
    }
  }, [filters.offset]);

  useEffect(() => {
    const list = listRef.current;
    if (list) {
      list.addEventListener('scroll', handleScroll);
      return () => {
        list.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isLoading, files, totalFiles]);

  /**
   * @desc Get the current folder parent
   */
  const currentFolderParent = useMemo(() => {
    if (parent?.path && parent.path.length > 0) {
      const path = parent.path;
      return {
        ...path[path.length - 2],
        title: path[path.length - 1].title,
      };
    }
    return null;
  }, [parent]);

  /**
   * @desc Get the selected file ids
   */
  const selectedFileId = useMemo(() => selectedFile?.fileId, [selectedFile]);

  /**
   * @desc Check if the file is disabled
   * @param file - The file
   * @returns Whether the file is disabled
   */
  const isFileDisabled = useCallback(
    (file: File) => {
      if (file.readOnly || file.isQuickShare) {
        return true;
      }

      return (
        !file.isFolder ||
        selectedFileId === file.fileId ||
        selectedFile?.ownerId !== file?.ownerId
      );
    },
    [selectedFile, selectedFileId]
  );

  return (
    <div>
      <div className="text-sm text-gray-dark">
        <span>{t('modal.fileShortcutForm.currentPosition')}:</span>
        <span className="ml-1">
          {currentFolder?.title || t('common.files')}
        </span>
      </div>
      <div
        className={clsx(
          'flex flex-row items-center pt-2',
          !currentFolderParent && 'opacity-0'
        )}
      >
        <Button
          type="text"
          className="p-0"
          onClick={() => {
            setSelectedFolder(null);
            setFilters({
              ...filters,
              offset: 0,
              parent: currentFolderParent?.fileId || undefined,
            });
          }}
        >
          <Icon name="ChevronLeftIcon" className="h-6 w-6" />
        </Button>
        <span className="ml-1">{currentFolderParent?.title}</span>
      </div>
      <div className="border-b border-gray-disabled" />
      {files.length === 0 && !isLoading ? (
        <div
          className="flex items-center justify-center overflow-hidden"
          style={{ height: '320px' }}
        >
          <div className="flex flex-col items-center justify-center gap-2">
            <Icon name="FolderIcon" className="h-36 w-36 opacity-25" />
            <span className="text-gray-400 text-sm">
              {t('modal.fileShortcutForm.folderIsEmpty')}
            </span>
          </div>
        </div>
      ) : (
        <ul
          className="overflow-x-hidden overflow-y-scroll pt-1"
          style={{ height: '320px' }}
          ref={listRef}
        >
          <Spin spinning={isLoading}>
            <li
              className={clsx(
                'flex cursor-pointer flex-row items-center justify-between rounded pb-1 pl-1 pr-1 pt-1 text-blue-dark hover:bg-gray',
                !selectedFolder && 'bg-light'
              )}
              onClick={() => {
                setSelectedFolder(null);
                setFilters({ ...filters, offset: 0, parent: undefined });
              }}
              onDoubleClick={() => {
                setFilters({ ...filters, offset: 0, parent: undefined });
              }}
            >
              <div className="flex flex-row items-center p-1">
                <div className="">
                  <FileIcon
                    isFolder={true}
                    fileExtension={''}
                    showExtension={true}
                    style={{ height: 20, width: 35 }}
                  />
                </div>
                <span className="p-1 text-azure">{t('common.files')}</span>
              </div>
            </li>

            {files.map((f) => (
              <li
                className={clsx(
                  'flex flex-row items-center justify-between rounded pb-1 pl-1 pr-1 pt-1',
                  isFileDisabled(f)
                    ? 'pointer-events-none opacity-50'
                    : 'cursor-pointer hover:bg-gray',
                  selectedFolder?.fileId === f.fileId && 'bg-light'
                )}
                onDoubleClick={() => {
                  if (f.isFolder) {
                    setFilters({ ...filters, offset: 0, parent: f.fileId });
                  }
                }}
                onClick={() => {
                  if (f.isFolder) {
                    setSelectedFolder({ ...f, path: parent?.path });
                  }
                }}
                key={f.uid}
              >
                <div
                  className="flex flex-row items-center p-1"
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <FileIcon
                    isFolder={f.isFolder}
                    fileExtension={f.fileExtension}
                    style={{
                      height: 20,
                      width: 35,
                      color: getFolderHex(f),
                    }}
                  />
                  <span className="w-full truncate p-1">
                    <TruncatedTooltip>{getFileTitle(f)}</TruncatedTooltip>
                  </span>
                </div>
                <div className="ml-auto flex-shrink-0">
                  {f.isFolder && (
                    <Button
                      type="text"
                      onClick={() => {
                        if (f.isFolder) {
                          setSelectedFolder(f);
                          setFilters({
                            ...filters,
                            offset: 0,
                            parent: f.fileId,
                          });
                        }
                      }}
                    >
                      <Icon name="ChevronRightIcon" className="w-5" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </Spin>
        </ul>
      )}
      <div className="border-t border-gray-disabled" />
      <div className="overflow-x-auto pt-2 text-sm">
        {!selectedFolder ? (
          <span>
            {t('modal.fileShortcutForm.selectFolderToCreateShortcutInto')}
          </span>
        ) : (
          <div className="flex flex-row gap-2">
            {selectedFolder.fileId !== null && (
              <span
                className="underline-hover cursor-pointer"
                onClick={() => {
                  setFilters({ ...filters, offset: 0, parent: undefined });
                  setSelectedFolder(null);
                }}
              >
                {t('common.files')} /
              </span>
            )}
            {selectedFolder?.path?.map((folder) => (
              <span
                className={clsx(
                  folder.fileId === selectedFolder.fileId
                    ? 'pointer-events-none'
                    : 'underline-hover cursor-pointer'
                )}
                onClick={() => {
                  setFilters({ ...filters, offset: 0, parent: folder.fileId });
                  //setSelectedFolder(folder);
                }}
                key={folder.fileId}
              >
                {folder.title} /{' '}
              </span>
            ))}
            <span>{selectedFolder?.title}</span>
          </div>
        )}
      </div>
      <div className="mt-4 flex flex-row items-center justify-end gap-2">
        <Button size="middle" onClick={() => onCancel()}>
          {t('common.cancel')}
        </Button>
        <Button
          size="middle"
          type="primary"
          onClick={() => {
            onSubmit(selectedFolder?.uid || null);
          }}
        >
          {t('common.create')}
        </Button>
      </div>
    </div>
  );
};

export default FileShortcutForm;
